import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { addDays, addMonths, endOfMonth, format, isBefore, isSameDay, isSameMonth, startOfDay, startOfMonth, subMonths, } from 'date-fns';
import { useEffect, useMemo, useRef, useState, } from 'react';
import styled, { css } from 'styled-components';
import { LeftCaret, RightCaret } from './Icon';
export function Calendar({ value: valueProp, onChange }) {
    const [now, setNow] = useState();
    useEffect(() => {
        setNow(Date.now());
    }, []);
    const value = useMemo(() => sanitizeDate(valueProp), [valueProp]);
    const [currentDate, setCurrentDate] = useState(value);
    const [selectedDate, setSelectedDate] = useState(value);
    const [highlightedDate, setHighlightedDate] = useState(value);
    useEffect(() => {
        setSelectedDate(value);
        setHighlightedDate(value);
    }, [value]);
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth(); // 0-indexed
    const firstDayOfMonth = new Date(year, month, 1);
    const startingDay = firstDayOfMonth.getDay(); // 0 = Sunday
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const monthName = currentDate.toLocaleString('default', { month: 'long' });
    const totalDays = ((x) => (x % 7 === 0 ? x : x + 7 - ((x + 7) % 7)))(startingDay + daysInMonth);
    const [currentYear, setCurrentYear] = useState();
    useEffect(() => {
        setCurrentYear(new Date().getFullYear());
    }, []);
    useEffect(() => {
        setCurrentDate(highlightedDate);
    }, [highlightedDate]);
    const currentDateRef = useRef(currentDate);
    if (currentDateRef.current !== currentDate) {
        currentDateRef.current = currentDate;
    }
    useEffect(function keyboard() {
        function onKeyDown(e) {
            const offset = e.key === 'ArrowUp'
                ? -7
                : e.key === 'ArrowDown'
                    ? 7
                    : e.key === 'ArrowLeft'
                        ? -1
                        : e.key === 'ArrowRight'
                            ? 1
                            : undefined;
            if (offset == null) {
                return;
            }
            e.preventDefault();
            setHighlightedDate((c) => {
                if (isSameMonth(c, currentDateRef.current)) {
                    return sanitizeDate(addDays(c, offset));
                }
                return sanitizeDate((offset > 0 ? startOfMonth : endOfMonth)(currentDateRef.current));
            });
        }
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, []);
    return (_jsxs(CalendarRoot, { children: [_jsxs(Header, { children: [_jsx(DirectionButton, { type: "button", onClick: () => {
                            setCurrentDate((c) => sanitizeDate(subMonths(c, 1)));
                        }, disabled: !now || isBefore(startOfMonth(currentDate), now), children: _jsx(LeftCaret, {}) }), _jsx("h2", { children: year === currentYear ? (_jsx(_Fragment, { children: monthName })) : (_jsxs(_Fragment, { children: [monthName, " ", year] })) }), _jsx(DirectionButton, { type: "button", onClick: () => {
                            setCurrentDate((c) => sanitizeDate(addMonths(c, 1)));
                        }, children: _jsx(RightCaret, {}) })] }), _jsxs(Cols, { children: [_jsx(DayName, { children: "S" }), _jsx(DayName, { children: "M" }), _jsx(DayName, { children: "T" }), _jsx(DayName, { children: "W" }), _jsx(DayName, { children: "T" }), _jsx(DayName, { children: "F" }), _jsx(DayName, { children: "S" }), [...Array(totalDays)].map((_, i) => {
                        const date = startOfDay(addDays(firstDayOfMonth, i - startingDay));
                        const disabled = now == null || isBefore(date, startOfDay(now));
                        return (_jsx(Day, { empty: disabled || !isSameMonth(date, firstDayOfMonth), today: !!now && isSameDay(now, date), date: date, onClick: () => {
                                if (startOfDay(Date.now()).getTime() >
                                    date.getTime()) {
                                    return;
                                }
                                setSelectedDate(date);
                                setHighlightedDate(date);
                                onChange?.(date);
                            }, onFocus: () => {
                                setHighlightedDate(sanitizeDate(date));
                            }, highlighted: highlightedDate
                                ? isSameDay(highlightedDate, date)
                                : false, selected: selectedDate
                                ? isSameDay(selectedDate, date)
                                : false }, format(date, 'd/M')));
                    })] })] }));
}
function sanitizeDate(date) {
    return new Date(Math.max(Date.now(), new Date(date).getTime()));
}
const DirectionButton = styled.button `
    appearance: none;
    border: 0;
    background: 0;
    width: 2rem;
    height: 2rem;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: 200ms opacity;

    &:disabled {
        opacity: 0.3;
    }
`;
function Day({ date, empty = false, selected = false, highlighted = false, today = false, ...props }) {
    const rootRef = useRef(null);
    useEffect(() => {
        if (highlighted) {
            rootRef.current?.focus();
        }
    }, [highlighted]);
    return (_jsx(DayRoot, { ...props, "$dimm": empty, "$selected": selected, "$highlight": highlighted, "$today": today, ref: rootRef, children: format(date, 'd') }));
}
const DayRoot = styled.button `
    appearance: none;
    background: none;
    align-items: center;
    border: 0;
    border-radius: 50%;
    color: #ffffff;
    display: flex;
    font-size: 0.75rem;
    font-weight: 500;
    height: 1.875rem;
    outline: 0;
    justify-content: center;
    width: 1.875rem;
    transition: 100ms background-color;

    &:focus-visible {
        background: rgba(255, 255, 255, 0.1);
    }

    ${({ $dimm }) => $dimm &&
    css `
            opacity: 0.3;
        `}

    ${({ $highlight }) => $highlight &&
    css `
            background: rgba(255, 255, 255, 0.1);
        `}

    ${({ $today }) => $today &&
    css `
            background: rgba(255, 255, 255, 0.05);
        `}

    ${({ $selected }) => $selected &&
    css `
            background: #ff5924 !important;

            &:focus-visible {
                box-shadow: 0 0 0 0.1875rem rgba(255, 255, 255, 0.3);
            }
        `}
`;
const DayName = styled.div `
    align-items: center;
    border-radius: 50%;
    color: #ffffff;
    display: flex;
    font-size: 0.625rem;
    font-weight: 500;
    height: 1.875rem;
    justify-content: center;
    opacity: 0.7;
    width: 1.875rem;
`;
const Header = styled.div `
    display: grid;
    grid-template-columns: auto 1fr auto;

    h2 {
        font-size: 1.125rem;
        text-align: center;
        font-weight: 500;
    }
`;
const Cols = styled.div `
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 0.125rem;
    width: 14rem;
    padding: 0.125rem;
    margin-top: 0.625rem;
`;
const CalendarRoot = styled.div `
    padding: 0.625rem;
    user-select: none;
`;
